<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import UIIcon from './UIIcon';
import UIFormItem from './UIFormItem';
import UIButton from './UIButton';
import RatioContainer from '../utils/RatioContainer';
import { ValidationProvider } from 'vee-validate';
import Loading from '../Loading';
import UIInput from '@/core/shared/components/ui/UIInput';

const RatioContainerWrapper = {
  name: 'RatioContainerWrapper',
  functional: true,
  render: (h, { props, children }) => {
    return props.ratio ? h(RatioContainer, { props: { ratio: props.ratio } }, children) : children;
  },
};

@Component({
  inheritAttrs: false,
  components: {
    UIIcon,
    UIFormItem,
    UIButton,
    RatioContainerWrapper,
    Loading,
    ValidationProvider,
    UIInput,
  },
  props: {
    disabled: Boolean,
    loading: Boolean,
    file: Object,
    specs: String,
    ratio: Number,
    rules: {
      type: [String, Object],
      default: '',
    },
    fieldNameOverride: String,
    vid: {
      type: String,
      default: undefined,
    },
    ...UIFormItem.options.props,
  },
})
export default class UILinkPreview extends Vue {
  validationDisabled = false;
  inputValue = '';

  created () {
    this.validationDisabled = !!this.$props.file;
    if (this.file && this.file.url) {
      this.inputValue = this.file.url;
    }
  }

  async updateLink () {
    await this.syncValidationInfo(this.inputValue);
    this.$emit('change', this.inputValue);
  }

  async clearLink () {
    this.inputValue = '';
    await this.syncValidationInfo(null);
    this.$emit('clear');
  }

  async syncValidationInfo (value) {
    this.validationDisabled = false;
    this.$refs.validationProvider.syncValue(value);
    const validationResult = await this.$refs.validationProvider.validate();
    this.$refs.validationProvider.applyResult(validationResult);
    return validationResult;
  }

  get isVideo () {
    if (!this.inputValue) return false;
    return /\.(mp4|webm|ogg)$/i.test(this.inputValue);
  }

  get isImage () {
    if (!this.inputValue) return false;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(this.inputValue);
  }
}
</script>

<template>
  <ValidationProvider
    :disabled="validationDisabled"
    :detectInput="false"
    tag="div"
    :rules="rules"
    v-slot="{ errors }"
    :vid="vid"
    :name="!fieldNameOverride ? title : fieldNameOverride"
    ref="validationProvider"
  >
    <UIFormItem
      :title="title"
      :instructions="instructions"
      :required="required"
      :root="true"
      :error="!!errors[0]"
      :error-msg="errors[0]"
    >
      <RatioContainerWrapper :ratio="ratio">
        <div class="upload-button relative h-100 overflow-hidden" :class="{'-loading': loading, '-error': !!errors[0], '-disabled': disabled}">
          <Loading class="absolute absolute-center" v-if="loading"></Loading>

          <div v-show="!loading" style="width: 100%">
            <div v-if="file && file.url && isImage" class="bgimg contain bg-center absolute absolute--fill"
                 :style="{ backgroundImage: `url('${file.thumbnail || file.url}')` }"></div>
            <video v-else-if="file && file.url && isVideo" class="absolute absolute--fill" controls :src="file.url"></video>

            <UIInput
              v-if="!inputValue"
              class="fullrow"
              v-model="inputValue"
              :disabled="disabled"
              :required="false"
              v-bind="$attrs"
              @input="updateLink"
              :placeholder="$t('Enter a URL...')"
            />
          </div>
        </div>
      </RatioContainerWrapper>

      <p class="flex items-center mt2" v-if="inputValue">
        <span class="f-thead mr-auto w-70 truncate">{{ inputValue }}</span>
        <UIButton class="dib fill-manatee" v-if="!disabled" @click="clearLink" tiny="tiny" light="light" icon="trash" />
      </p>
      <p class="tr f-thead mr-auto mt2" v-else-if="specs">{{ specs }}</p>
    </UIFormItem>
  </ValidationProvider>
</template>

<style lang="postcss" scoped>
.input-link {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--colorBorder);
  border-radius: 4px;
  margin-top: 0.5rem;
}
.upload-button {
  @apply bg-transparent border border-gray-300 text-gray-600 rounded px-3 py-1;
  @apply flex items-center justify-center text-center relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  &.-loading {
    pointer-events: none;
  }
  &.-error {
    border-color: var(--colorErrorFlat);
    box-shadow: none;
    border-width: 2px;
  }
  &.-disabled {
    cursor: not-allowed;
  }
}
.absolute-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.bgimg {
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
